import React, { useState, useEffect, useRef } from "react"
import { useGuias } from "../context/GuiasContext"
import {FileCheck, Printer, Save, X, Download, Building2} from 'lucide-react'
import toast from "react-hot-toast"

interface EmitirAlvaraProps {
  empresaId?: string
  editingAlvaraId?: string
  onSaveComplete?: () => void
}

const MARCA_DAGUA = "https://static.lumi.new/e9/e94f3c255c99cf8881ab95aac3edd2b0.webp"

function formatDate(iso: string) {
  if (!iso) return ""
  const d = new Date(iso + "T12:00:00")
  return d.toLocaleDateString("pt-BR")
}

function formatDateInput(iso: string) {
  if (!iso) return ""
  return iso.slice(0, 10)
}

export default function EmitirAlvara({ empresaId, editingAlvaraId, onSaveComplete }: EmitirAlvaraProps) {
  const { empresas, getEmpresaById, alvaras, addAlvara, updateAlvara, getAlvaraById, logoUrl, dadosPrefeitura } = useGuias()
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  const hoje = new Date().toISOString().slice(0, 10)
  const proximoAno = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10)
  const anoAtual = new Date().getFullYear().toString()

  const emptyForm = {
    razaoSocial: "", nomeFantasia: "", cnpj: "", tipoEmpresa: "",
    ramoAtividade: "", cnae: "", inscricaoMunicipal: "",
    responsavel: "", cpfResponsavel: "",
    endereco: "", numero: "", complemento: "", bairro: "",
    cidade: "São José do Seridó", uf: "RN", cep: "",
    telefone: "", areaM2: "",
    dataEmissao: hoje, dataValidade: proximoAno,
    anoExercicio: anoAtual,
    taxaAlvara: "", observacoes: ""
  }

  const [form, setForm] = useState(emptyForm)
  const [numeroAlvara, setNumeroAlvara] = useState("")

  useEffect(() => {
    if (editingAlvaraId) {
      const a = getAlvaraById(editingAlvaraId)
      if (a) {
        setNumeroAlvara(a.numeroAlvara || "")
        setForm({
          razaoSocial: a.razaoSocial || "",
          nomeFantasia: a.nomeFantasia || "",
          cnpj: a.cnpj || "",
          tipoEmpresa: a.tipoEmpresa || "",
          ramoAtividade: a.ramoAtividade || "",
          cnae: a.cnae || "",
          inscricaoMunicipal: a.inscricaoMunicipal || "",
          responsavel: a.responsavel || "",
          cpfResponsavel: a.cpfResponsavel || "",
          endereco: a.endereco || "",
          numero: a.numero || "",
          complemento: a.complemento || "",
          bairro: a.bairro || "",
          cidade: a.cidade || "São José do Seridó",
          uf: a.uf || "RN",
          cep: a.cep || "",
          telefone: a.telefone || "",
          areaM2: a.areaM2 || "",
          dataEmissao: formatDateInput(a.dataEmissao || hoje),
          dataValidade: formatDateInput(a.dataValidade || proximoAno),
          anoExercicio: a.anoExercicio || anoAtual,
          taxaAlvara: a.taxaAlvara || "",
          observacoes: a.observacoes || ""
        })
        setSaved(true)
      }
    } else if (empresaId) {
      const empresa = getEmpresaById(empresaId)
      if (empresa) {
        setNumeroAlvara(`ALV${Date.now().toString().slice(-6)}`)
        setForm({
          razaoSocial: empresa.razaoSocial || "",
          nomeFantasia: empresa.nomeFantasia || "",
          cnpj: empresa.cnpj || "",
          tipoEmpresa: empresa.tipoEmpresa || "",
          ramoAtividade: empresa.ramoAtividade || "",
          cnae: empresa.cnae || "",
          inscricaoMunicipal: empresa.inscricaoMunicipal || "",
          responsavel: empresa.responsavel || "",
          cpfResponsavel: empresa.cpfResponsavel || "",
          endereco: empresa.endereco || "",
          numero: empresa.numero || "",
          complemento: empresa.complemento || "",
          bairro: empresa.bairro || "",
          cidade: empresa.cidade || "São José do Seridó",
          uf: empresa.uf || "RN",
          cep: empresa.cep || "",
          telefone: empresa.telefone || "",
          areaM2: empresa.areaM2 || "",
          dataEmissao: hoje,
          dataValidade: proximoAno,
          anoExercicio: anoAtual,
          taxaAlvara: "",
          observacoes: ""
        })
      }
    }
  }, [empresaId, editingAlvaraId, empresas, alvaras])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    if (!form.razaoSocial) { toast.error("Razão Social é obrigatória"); return }
    setLoading(true)
    try {
      const num = editingAlvaraId ? (getAlvaraById(editingAlvaraId)?.numeroAlvara || numeroAlvara) : numeroAlvara || `ALV${Date.now().toString().slice(-6)}`
      const payload = {
        ...form,
        empresaId: empresaId || "",
        numeroAlvara: num,
        statusAlvara: "ativo"
      }
      if (editingAlvaraId) {
        await updateAlvara(editingAlvaraId, payload)
      } else {
        await addAlvara(payload)
      }
      setSaved(true)
      toast.success("Alvará salvo com sucesso!")
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    const printContent = printRef.current?.innerHTML
    if (!printContent) return
    const w = window.open("", "_blank", "width=900,height=700")
    if (!w) return
    w.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Alvará de Funcionamento Nº ${numeroAlvara}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Times New Roman', Times, serif; background: white; }
          @page { size: A4; margin: 1.5cm; }
          @media print {
            img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        ${printContent}
      </body>
      </html>
    `)
    w.document.close()
  }

  const prefeitura = dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal de São José do Seridó"
  const secretaria = dadosPrefeitura?.secretaria || "Secretaria Municipal de Tributação"
  const endPref = dadosPrefeitura?.endereco || "Praça Severino Bezerra, s/n – Centro"

  const inputClass = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004161]/30 focus:border-[#004161] bg-white"
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1"

  return (
    <div className="space-y-6">
      {/* Formulário de configuração */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-[#004161] px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-white font-bold text-base">Emissão de Alvará de Funcionamento</h2>
            <p className="text-blue-200 text-xs">Nº {numeroAlvara || "Gerado ao salvar"}</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Dados Gerais */}
          <div>
            <h3 className="text-xs font-bold text-[#004161] uppercase tracking-wide mb-3">Dados do Estabelecimento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Razão Social *</label>
                <input name="razaoSocial" value={form.razaoSocial} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Nome Fantasia</label>
                <input name="nomeFantasia" value={form.nomeFantasia} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>CNPJ</label>
                <input name="cnpj" value={form.cnpj} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Tipo de Empresa</label>
                <input name="tipoEmpresa" value={form.tipoEmpresa} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Ramo de Atividade</label>
                <input name="ramoAtividade" value={form.ramoAtividade} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>CNAE</label>
                <input name="cnae" value={form.cnae} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Inscrição Municipal</label>
                <input name="inscricaoMunicipal" value={form.inscricaoMunicipal} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Área do Estabelecimento (m²)</label>
                <input name="areaM2" value={form.areaM2} onChange={handleChange} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Responsável */}
          <div>
            <h3 className="text-xs font-bold text-[#004161] uppercase tracking-wide mb-3">Responsável</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome do Responsável</label>
                <input name="responsavel" value={form.responsavel} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>CPF do Responsável</label>
                <input name="cpfResponsavel" value={form.cpfResponsavel} onChange={handleChange} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div>
            <h3 className="text-xs font-bold text-[#004161] uppercase tracking-wide mb-3">Endereço</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Logradouro</label>
                <input name="endereco" value={form.endereco} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Número</label>
                <input name="numero" value={form.numero} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Complemento</label>
                <input name="complemento" value={form.complemento} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Bairro</label>
                <input name="bairro" value={form.bairro} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Cidade</label>
                <input name="cidade" value={form.cidade} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>UF</label>
                <input name="uf" value={form.uf} onChange={handleChange} className={inputClass} maxLength={2} />
              </div>
              <div>
                <label className={labelClass}>CEP</label>
                <input name="cep" value={form.cep} onChange={handleChange} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Alvará */}
          <div>
            <h3 className="text-xs font-bold text-[#004161] uppercase tracking-wide mb-3">Dados do Alvará</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>Data de Emissão</label>
                <input type="date" name="dataEmissao" value={form.dataEmissao} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Data de Validade</label>
                <input type="date" name="dataValidade" value={form.dataValidade} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Exercício (Ano)</label>
                <input name="anoExercicio" value={form.anoExercicio} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Taxa do Alvará (R$)</label>
                <input name="taxaAlvara" value={form.taxaAlvara} onChange={handleChange} className={inputClass} placeholder="0,00" />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelClass}>Observações / Condicionantes</label>
              <textarea name="observacoes" value={form.observacoes} onChange={handleChange} rows={2} className={inputClass + " resize-none"} placeholder="Condições específicas do alvará..." />
            </div>
          </div>

          {/* Botões */}
          <div className="flex flex-col sm:flex-row gap-3">
            {onSaveComplete && (
              <button type="button" onClick={onSaveComplete}
                className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm font-medium">
                <X className="w-4 h-4" /> Voltar
              </button>
            )}
            <button onClick={handleSave} disabled={loading}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#004161] text-white rounded-xl hover:bg-[#003050] text-sm font-semibold disabled:opacity-50">
              <Save className="w-4 h-4" />
              {loading ? "Salvando..." : "Salvar Alvará"}
            </button>
            {saved && (
              <button onClick={handlePrint}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-semibold">
                <Printer className="w-4 h-4" /> Imprimir Alvará
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Preview do Alvará */}
      {saved && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-700">Pré-visualização do Alvará</h3>
            <button onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#004161] text-white rounded-lg text-xs font-semibold hover:bg-[#003050]">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
          </div>

          {/* Documento para impressão */}
          <div ref={printRef} style={{ fontFamily: "'Times New Roman', Times, serif" }}>
            <div style={{
              width: "210mm",
              minHeight: "297mm",
              margin: "0 auto",
              padding: "20mm 15mm",
              background: "white",
              position: "relative",
              boxSizing: "border-box"
            }}>
              {/* Marca d'água */}
              <img
                src={MARCA_DAGUA}
                alt=""
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "520px",
                  height: "520px",
                  opacity: 0.08,
                  objectFit: "contain",
                  pointerEvents: "none",
                  zIndex: 0
                }}
              />

              <div style={{ position: "relative", zIndex: 1 }}>
                {/* Cabeçalho */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "20px", marginBottom: "16px", borderBottom: "3px solid #004161", paddingBottom: "14px" }}>
                  <img src={logoUrl} alt="Brasão" style={{ height: "80px", width: "auto", flexShrink: 0 }} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "11px", fontWeight: "bold", color: "#004161", textTransform: "uppercase", letterSpacing: "2px" }}>ESTADO DO RIO GRANDE DO NORTE</div>
                    <div style={{ fontSize: "17px", fontWeight: "bold", color: "#004161", textTransform: "uppercase", marginTop: "3px", letterSpacing: "1px" }}>{prefeitura}</div>
                    <div style={{ fontSize: "11px", color: "#555", marginTop: "3px" }}>{secretaria}</div>
                    <div style={{ fontSize: "10px", color: "#777", marginTop: "2px" }}>{endPref}</div>
                    {dadosPrefeitura?.cnpj && <div style={{ fontSize: "10px", color: "#777" }}>CNPJ: {dadosPrefeitura.cnpj}</div>}
                  </div>
                </div>

                {/* Título do Alvará */}
                <div style={{ textAlign: "center", margin: "16px 0", padding: "12px", border: "2px solid #004161", borderRadius: "12px", background: "#f0f7ff" }}>
                  <div style={{ fontSize: "20px", fontWeight: "bold", color: "#004161", textTransform: "uppercase", letterSpacing: "2px" }}>
                    ALVARÁ DE FUNCIONAMENTO
                  </div>
                  <div style={{ fontSize: "14px", color: "#004161", marginTop: "4px" }}>
                    Nº <strong>{numeroAlvara}</strong> — Exercício: <strong>{form.anoExercicio}</strong>
                  </div>
                </div>

                {/* Texto de autorização */}
                <div style={{ fontSize: "12px", lineHeight: "1.8", color: "#333", textAlign: "justify", margin: "16px 0" }}>
                  <p>
                    A <strong>Prefeitura Municipal de São José do Seridó</strong>, Estado do Rio Grande do Norte, no uso de suas atribuições legais conferidas pela Lei Orgânica do Município e pelo Código Tributário Municipal, <strong>AUTORIZA</strong> o funcionamento do estabelecimento comercial/industrial/prestador de serviços abaixo identificado, nas condições e especificações descritas neste documento, em conformidade com a legislação municipal vigente.
                  </p>
                </div>

                {/* Dados do Estabelecimento */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "11px", marginBottom: "12px", borderRadius: "10px", overflow: "hidden", border: "1px solid #dde3ea" }}>
                  <thead>
                    <tr>
                      <td colSpan={4} style={{ background: "#004161", color: "white", padding: "7px 12px", fontWeight: "bold", fontSize: "12px", letterSpacing: "0.5px" }}>
                        IDENTIFICAÇÃO DO ESTABELECIMENTO
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8", width: "25%" }}>Razão Social</td>
                      <td colSpan={3} style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.razaoSocial}</td>
                    </tr>
                    {form.nomeFantasia && (
                      <tr>
                        <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Nome Fantasia</td>
                        <td colSpan={3} style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.nomeFantasia}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>CNPJ</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.cnpj}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Tipo</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.tipoEmpresa}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Insc. Municipal</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.inscricaoMunicipal || "—"}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>CNAE</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.cnae || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Ramo de Atividade</td>
                      <td colSpan={3} style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.ramoAtividade}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Responsável</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.responsavel || "—"}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>CPF Resp.</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.cpfResponsavel || "—"}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Endereço */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "11px", marginBottom: "12px", borderRadius: "10px", overflow: "hidden", border: "1px solid #dde3ea" }}>
                  <thead>
                    <tr>
                      <td colSpan={4} style={{ background: "#004161", color: "white", padding: "7px 12px", fontWeight: "bold", fontSize: "12px", letterSpacing: "0.5px" }}>
                        ENDEREÇO DO ESTABELECIMENTO
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8", width: "20%" }}>Logradouro</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.endereco}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8", width: "10%" }}>Nº</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", width: "15%" }}>{form.numero}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Bairro</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.bairro}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>CEP</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.cep}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Município</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.cidade}/{form.uf}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px", fontWeight: "bold", background: "#f8f8f8" }}>Área (m²)</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px 8px" }}>{form.areaM2 || "—"}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Dados do Alvará */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "11px", marginBottom: "12px", borderRadius: "10px", overflow: "hidden", border: "1px solid #dde3ea" }}>
                  <thead>
                    <tr>
                      <td colSpan={4} style={{ background: "#004161", color: "white", padding: "7px 12px", fontWeight: "bold", fontSize: "12px", letterSpacing: "0.5px" }}>
                        DADOS DO ALVARÁ
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", background: "#f5f8fb", width: "25%" }}>Número do Alvará</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", color: "#004161" }}>{numeroAlvara}</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", background: "#f5f8fb" }}>Exercício</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px" }}>{form.anoExercicio}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", background: "#f5f8fb" }}>Data de Emissão</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px" }}>{formatDate(form.dataEmissao)}</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", background: "#f5f8fb" }}>Válido até</td>
                      <td style={{ border: "1px solid #eee", padding: "6px 10px", fontWeight: "bold", color: "#007a33" }}>{formatDate(form.dataValidade)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Observações */}
                {form.observacoes && (
                  <div style={{ border: "1px solid #ccc", padding: "8px 10px", fontSize: "11px", marginBottom: "12px" }}>
                    <strong>CONDICIONANTES / OBSERVAÇÕES:</strong>
                    <p style={{ marginTop: "4px", lineHeight: "1.6" }}>{form.observacoes}</p>
                  </div>
                )}

                {/* Texto legal */}
                <div style={{ fontSize: "10px", lineHeight: "1.6", color: "#555", textAlign: "justify", marginBottom: "20px", borderTop: "1px solid #ddd", paddingTop: "10px" }}>
                  <p>Este documento é válido somente para o exercício de <strong>{form.anoExercicio}</strong>, para a atividade e endereço acima especificados, devendo ser renovado anualmente. O estabelecimento deverá manter este documento em local visível ao público. O descumprimento das condições previstas neste alvará sujeitará o titular às sanções previstas na legislação municipal.</p>
                </div>

                {/* Assinaturas */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", fontSize: "11px" }}>
                  <div style={{ textAlign: "center", width: "40%" }}>
                    <div style={{ borderTop: "1px solid #333", paddingTop: "6px" }}>
                      <div style={{ fontWeight: "bold" }}>Secretário(a) de Tributação</div>
                      <div style={{ fontSize: "10px", color: "#777" }}>Prefeitura de São José do Seridó/RN</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "10px", color: "#777", marginBottom: "4px" }}>São José do Seridó/RN, {formatDate(form.dataEmissao)}</div>
                  </div>
                  <div style={{ textAlign: "center", width: "40%" }}>
                    <div style={{ borderTop: "1px solid #333", paddingTop: "6px" }}>
                      <div style={{ fontWeight: "bold" }}>Prefeito(a) Municipal</div>
                      <div style={{ fontSize: "10px", color: "#777" }}>Prefeitura de São José do Seridó/RN</div>
                    </div>
                  </div>
                </div>

                {/* Rodapé */}
                <div style={{ marginTop: "20px", borderTop: "2px solid #004161", paddingTop: "8px", textAlign: "center", fontSize: "9px", color: "#999" }}>
                  <p>Sistema EMIT — Prefeitura Municipal de São José do Seridó/RN | {dadosPrefeitura?.telefone || ""} | saojosedoserido.rn.gov.br</p>
                  <p>Documento emitido eletronicamente. Em caso de dúvidas, entre em contato com a Secretaria de Tributação.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
